import { sql } from "drizzle-orm";
import {
  check,
  index,
  integer,
  real,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { timestamps } from "@/db/columns.helpers";
import type { MediaData } from "@/db/schema/hiive";
import { hiive } from "@/db/schema/hiive";
import type { SocialLinks } from "@/lib/contact-links";

export const VENUE_STATUS = [
  /** Visible only to the hiive's team until it is submitted. */
  "draft",
  "pending_approval",
  "active",
  "inactive",
  "archived",
  "rejected",
] as const;
export type VenueStatus = (typeof VENUE_STATUS)[number];

export const VENUE_SPOT_STATUS = [
  "available",
  "maintenance",
  "retired",
] as const;
export type VenueSpotStatus = (typeof VENUE_SPOT_STATUS)[number];

export const VENUE_ADDON_CHARGE_MODE = [
  "per_booking",
  "per_hour",
  "per_person",
] as const;
export type VenueAddonChargeMode = (typeof VENUE_ADDON_CHARGE_MODE)[number];

export type VenueStructuredListItem = {
  title: string;
  description?: string | null;
};

export const venue = sqliteTable(
  "venue",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    hiiveId: integer("hiive_id")
      .notNull()
      .references(() => hiive.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    slug: text("slug").notNull(),
    description: text("description").notNull().default(""),

    bannerMedia: text("banner_media", { mode: "json" })
      .$type<MediaData[]>()
      .notNull()
      .default(sql`'[]'`),

    address: text("address").notNull(),
    latitude: real("latitude").notNull(),
    longitude: real("longitude").notNull(),
    // Nullable so the migration is safe on existing rows; backfilled and then
    // auto-generated on write (precision 4, matching hiiveLocation/event).
    geohash: text("geohash"),
    timeZone: text("time_zone").notNull().default("Asia/Kolkata"),
    currency: text("currency").notNull().default("INR"),

    status: text("status")
      .notNull()
      .$type<VenueStatus>()
      .default("pending_approval"),

    policy: text("policy"),
    bookingNotes: text("booking_notes"),
    // Prose shown to members. The enforceable ladder lives on refundPolicyId
    // below; this survives as that policy's human-readable notes.
    // Active refund policy. Not a FK — see the note on event.refundPolicyId.
    // NULL falls back to the owning hiive's default.
    refundPolicyId: integer("refund_policy_id"),
    amenities: text("amenities", { mode: "json" })
      .$type<string[]>()
      .notNull()
      .default(sql`'[]'`),
    termsAndConditions: text("terms_and_conditions", { mode: "json" })
      .$type<VenueStructuredListItem[]>()
      .notNull()
      .default(sql`'[]'`),
    faqs: text("faqs", { mode: "json" })
      .$type<VenueStructuredListItem[]>()
      .notNull()
      .default(sql`'[]'`),

    contactPhone: text("contact_phone"),
    contactEmail: text("contact_email"),
    websiteUrl: text("website_url"),
    // Social handles, keyed by platform id — see lib/contact-links.ts. JSON so
    // adding a platform never needs a migration.
    socialLinks: text("social_links", { mode: "json" })
      .$type<SocialLinks>()
      .notNull()
      .default(sql`'{}'`),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    ...timestamps,
  },
  (t) => [
    uniqueIndex("venue_hiive_slug_unique").on(t.hiiveId, t.slug),
    index("venue_by_hiive").on(t.hiiveId),
    index("venue_by_status").on(t.status),
    index("venue_geohash_idx").on(t.geohash),
  ],
);

// Physical inventory. A "spot" is a bookable physical thing at a venue — a
// badminton court, a bouldering wall, a yoga mat slot, a diving cage. The
// customer-facing label (Court / Mat / Wall / Lane) is per-experience.
export const venueSpot = sqliteTable(
  "venue_spot",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    venueId: integer("venue_id")
      .notNull()
      .references(() => venue.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    sortOrder: integer("sort_order").notNull().default(0),
    status: text("status")
      .notNull()
      .$type<VenueSpotStatus>()
      .default("available"),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    ...timestamps,
  },
  (t) => [
    index("venue_spot_by_venue").on(t.venueId),
    index("venue_spot_by_venue_active").on(t.venueId, t.isActive),
    index("venue_spot_sort_idx").on(t.venueId, t.sortOrder),
  ],
);

// Optional extras that can be added to a booking — racket rental, shoe rental,
// helmet, instructor add-on, towel, etc.
export const venueAddon = sqliteTable(
  "venue_addon",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    venueId: integer("venue_id")
      .notNull()
      .references(() => venue.id, { onDelete: "cascade" }),

    name: text("name").notNull(),
    description: text("description"),
    chargeMode: text("charge_mode")
      .notNull()
      .$type<VenueAddonChargeMode>()
      .default("per_booking"),
    unitPriceMinor: integer("unit_price_minor").notNull().default(0),
    currency: text("currency").notNull().default("INR"),
    maxPerBooking: integer("max_per_booking"),

    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),
    sortOrder: integer("sort_order").notNull().default(0),

    metadata: text("metadata", { mode: "json" }).$type<
      Record<string, unknown>
    >(),

    ...timestamps,
  },
  (t) => [
    index("venue_addon_by_venue").on(t.venueId),
    index("venue_addon_by_venue_active").on(t.venueId, t.isActive),
    check(
      "venue_addon_max_per_booking_check",
      sql`${t.maxPerBooking} is null or ${t.maxPerBooking} >= 1`,
    ),
    check("venue_addon_unit_price_check", sql`${t.unitPriceMinor} >= 0`),
  ],
);

// Admin-declared closure windows. If spotId is null, the entire venue is closed.
export const venueClosure = sqliteTable(
  "venue_closure",
  {
    id: integer("id").primaryKey({ autoIncrement: true }),
    venueId: integer("venue_id")
      .notNull()
      .references(() => venue.id, { onDelete: "cascade" }),
    spotId: integer("spot_id").references(() => venueSpot.id, {
      onDelete: "cascade",
    }),
    experienceId: integer("experience_id"),

    startsAt: integer("starts_at", { mode: "timestamp" }).notNull(),
    endsAt: integer("ends_at", { mode: "timestamp" }).notNull(),
    reason: text("reason"),
    isActive: integer("is_active", { mode: "boolean" }).notNull().default(true),

    ...timestamps,
  },
  (t) => [
    index("venue_closure_by_venue").on(t.venueId),
    index("venue_closure_by_spot").on(t.spotId),
    index("venue_closure_by_experience").on(t.experienceId),
    index("venue_closure_by_active_time").on(
      t.venueId,
      t.isActive,
      t.startsAt,
      t.endsAt,
    ),
    check("venue_closure_time_order_check", sql`${t.startsAt} < ${t.endsAt}`),
  ],
);

export type Venue = typeof venue.$inferSelect;
export type VenueInsert = typeof venue.$inferInsert;
export type VenueSpot = typeof venueSpot.$inferSelect;
export type VenueSpotInsert = typeof venueSpot.$inferInsert;
export type VenueAddon = typeof venueAddon.$inferSelect;
export type VenueAddonInsert = typeof venueAddon.$inferInsert;
export type VenueClosure = typeof venueClosure.$inferSelect;
export type VenueClosureInsert = typeof venueClosure.$inferInsert;
